/* Query and mutation hooks for the ledger — STORY-020.
 *
 * The totals come from the server with every read, and are never recomputed
 * here. A second implementation of a sum is a second implementation that can
 * disagree with the first — and on this screen the sums are the product.
 */

import {
  type CreateCategoryRequest,
  type CreateTransactionRequest,
  categoriesResponseSchema,
  categorySchema,
  dashboardResponseSchema,
  eventMoneyResponseSchema,
  type ListTransactionsQuery,
  reconciliationResponseSchema,
  transactionSchema,
  transactionsResponseSchema,
  type UpdateTransactionRequest,
} from '@eventuley/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../../shared/api/client.js';

export const ledgerKey = (organisationId: string, query?: ListTransactionsQuery) =>
  query === undefined
    ? (['organisations', organisationId, 'transactions'] as const)
    : (['organisations', organisationId, 'transactions', query] as const);

export const categoriesKey = (organisationId: string) =>
  ['organisations', organisationId, 'categories'] as const;

const path = (organisationId: string) => `/organisations/${organisationId}/transactions`;

const toSearchParams = (query: ListTransactionsQuery): string => {
  const params = new URLSearchParams();
  if (query.eventId) params.set('eventId', query.eventId);
  if (query.attribution) params.set('attribution', query.attribution);
  if (query.currency) params.set('currency', query.currency);
  if (query.direction) params.set('direction', query.direction);
  if (query.categoryId) params.set('categoryId', query.categoryId);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));
  const rendered = params.toString();
  return rendered === '' ? '' : `?${rendered}`;
};

/**
 * The Categories this workspace uses.
 *
 * Cached separately from the ledger and for much longer in practice: they
 * change when somebody edits them, not when money is recorded.
 */
export function useCategories(organisationId: string) {
  return useQuery({
    queryKey: categoriesKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}/categories`, {
        schema: categoriesResponseSchema,
      }),
    retry: false,
  });
}

export const eventMoneyKey = (organisationId: string, eventId: string) =>
  ['organisations', organisationId, 'events', eventId, 'money'] as const;

/**
 * What one Event made — STORY-022.
 *
 * Under the Event's own key rather than the ledger's, so recording money
 * elsewhere in the workspace does not refetch every Event's figures. The
 * ledger's invalidation reaches it because both live under the same
 * organisation prefix.
 */
export function useEventMoney(organisationId: string, eventId: string) {
  return useQuery({
    queryKey: eventMoneyKey(organisationId, eventId),
    queryFn: () =>
      request(`/organisations/${organisationId}/events/${eventId}/money`, {
        schema: eventMoneyResponseSchema,
      }),
    retry: false,
  });
}

export const reconciliationKey = (organisationId: string) =>
  ['organisations', organisationId, 'reconciliation'] as const;

export const dashboardKey = (organisationId: string) =>
  ['organisations', organisationId, 'dashboard'] as const;

/**
 * The nine headline metrics — STORY-024.
 *
 * One query rather than nine, matching one server read: nine requests could
 * straddle a write and produce a dashboard whose own numbers disagree, and
 * every one of them would still look plausible.
 */
export function useDashboard(organisationId: string) {
  return useQuery({
    queryKey: dashboardKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}/dashboard`, {
        schema: dashboardResponseSchema,
      }),
    retry: false,
  });
}

/**
 * Attributed, Overhead, and the total they add to — STORY-023.
 *
 * Its own query rather than a field on the ledger: the ledger is filtered and
 * paged, and these figures are neither. Deriving them from a page would give a
 * number that changed when somebody clicked "next".
 */
export function useReconciliation(organisationId: string) {
  return useQuery({
    queryKey: reconciliationKey(organisationId),
    queryFn: () =>
      request(`/organisations/${organisationId}/reconciliation`, {
        schema: reconciliationResponseSchema,
      }),
    retry: false,
  });
}

export function useLedger(organisationId: string, query: ListTransactionsQuery = {}) {
  return useQuery({
    queryKey: ledgerKey(organisationId, query),
    queryFn: () =>
      request(`${path(organisationId)}${toSearchParams(query)}`, {
        schema: transactionsResponseSchema,
      }),
    /* The previous page stays while the next loads, so paging and filtering do
     * not blank the ledger and bounce the scroll position. */
    placeholderData: (previous) => previous,
    retry: false,
  });
}

/**
 * Every write invalidates the whole ledger prefix.
 *
 * Not just the page it was made from, unlike the guest list: a Transaction can
 * land on any page once the ledger is sorted by date, and every total moves
 * with it. Writing one page back and trusting the rest would show a figure that
 * does not match the rows under it, which on this screen is the one thing that
 * must never happen.
 */
const refresh = (queryClient: ReturnType<typeof useQueryClient>, organisationId: string) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: ledgerKey(organisationId) }),
    /* An Event's figures are derived from these rows, so a write moves them
     * too — and a screen showing a profit that disagrees with the entries
     * under it is the one thing this feature must never do. */
    queryClient.invalidateQueries({
      queryKey: ['organisations', organisationId, 'events'] as const,
    }),
    /* And the reconciliation, for the same reason and more sharply: recording
     * an overhead cost on the Entries tab moves all three of its figures, and
     * without this the panel mounted on cached numbers that predated the
     * write. A background refetch corrected them a moment later — or, if it
     * failed, did not, and the screen whose entire subject is figures that
     * agree showed figures that did not. */
    queryClient.invalidateQueries({ queryKey: reconciliationKey(organisationId) }),
    /* The dashboard reads the same rows — STORY-024. */
    queryClient.invalidateQueries({ queryKey: dashboardKey(organisationId) }),
  ]);

export function useRecordTransaction(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateTransactionRequest) =>
      request(path(organisationId), {
        method: 'POST',
        schema: transactionSchema,
        body: values,
      }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

export function useUpdateTransaction(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      transactionId,
      ...values
    }: UpdateTransactionRequest & { transactionId: string }) =>
      request(`${path(organisationId)}/${transactionId}`, {
        method: 'PATCH',
        schema: transactionSchema,
        body: values,
      }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

export function useDeleteTransaction(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (transactionId: string) =>
      request(`${path(organisationId)}/${transactionId}`, { method: 'DELETE' }),
    onSuccess: () => refresh(queryClient, organisationId),
  });
}

/**
 * Every write to a Category refreshes the ledger as well as the list.
 *
 * A rename reaches every Transaction filed under it — that is the point of a
 * Category being a record — so a cache holding only the old name would show the
 * rename on one screen and not on the rows it was made for.
 */
const refreshCategories = (
  queryClient: ReturnType<typeof useQueryClient>,
  organisationId: string,
) =>
  Promise.all([
    queryClient.invalidateQueries({ queryKey: categoriesKey(organisationId) }),
    queryClient.invalidateQueries({ queryKey: ledgerKey(organisationId) }),
  ]);

export function useCreateCategory(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (values: CreateCategoryRequest) =>
      request(`/organisations/${organisationId}/categories`, {
        method: 'POST',
        schema: categorySchema,
        body: values,
      }),
    onSuccess: () => refreshCategories(queryClient, organisationId),
  });
}

export function useRenameCategory(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ categoryId, name }: { categoryId: string; name: string }) =>
      request(`/organisations/${organisationId}/categories/${categoryId}`, {
        method: 'PATCH',
        schema: categorySchema,
        body: { name },
      }),
    onSuccess: () => refreshCategories(queryClient, organisationId),
  });
}

export function useDeleteCategory(organisationId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: string) =>
      request(`/organisations/${organisationId}/categories/${categoryId}`, { method: 'DELETE' }),
    onSuccess: () => refreshCategories(queryClient, organisationId),
  });
}
